import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Typography,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Send as SendIcon,
  Refresh as ResetIcon,
  SmartToy as BotIcon,
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  GraphicEq as AudioWaveIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import api from "../../services/api";

// [ANTIGRAVITY] Plan 899 — AI Simulator Panel for FlowBuilder

const useStyles = makeStyles((theme) => ({
  root: {
    width: 320,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.paper,
    borderLeft: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    flexShrink: 0,
    transition: "width 0.3s ease",
  },
  header: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 600,
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
  },
  modelBadge: {
    fontSize: "0.7rem",
    height: 20,
    backgroundColor: theme.palette.type === "dark" ? "#7c3aed22" : "#ede9fe",
    color: theme.palette.type === "dark" ? "#a78bfa" : "#7c3aed",
    border: `1px solid ${theme.palette.type === "dark" ? "#7c3aed44" : "#c4b5fd"}`,
    fontWeight: 600,
    cursor: "default",
  },
  ragBadge: {
    fontSize: "0.65rem",
    height: 18,
    backgroundColor: theme.palette.type === "dark" ? "#05966922" : "#d1fae5",
    color: theme.palette.type === "dark" ? "#34d399" : "#059669",
    border: `1px solid ${theme.palette.type === "dark" ? "#05966944" : "#6ee7b7"}`,
    fontWeight: 600,
    cursor: "default",
  },
  chatArea: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    ...theme.scrollbarStyles,
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.disabled,
  },
  bubble: {
    maxWidth: "85%",
    padding: theme.spacing(1, 1.5),
    borderRadius: 12,
    fontSize: "0.85rem",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  userBubble: {
    alignSelf: "flex-end",
    backgroundColor: theme.palette.type === "dark" ? "#7c3aed" : "#7c3aed",
    color: "#ffffff",
    borderBottomRightRadius: 4,
  },
  aiBubble: {
    alignSelf: "flex-start",
    backgroundColor: theme.palette.type === "dark" ? "#2d2d2d" : "#f1f5f9",
    color: theme.palette.text.primary,
    borderBottomLeftRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
  errorBubble: {
    alignSelf: "flex-start",
    backgroundColor: theme.palette.type === "dark" ? "#7f1d1d22" : "#fee2e2",
    color: theme.palette.type === "dark" ? "#f87171" : "#dc2626",
    borderBottomLeftRadius: 4,
    border: `1px solid ${theme.palette.type === "dark" ? "#7f1d1d" : "#fca5a5"}`,
  },
  messageRow: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.25),
  },
  msgLabel: {
    fontSize: "0.65rem",
    color: theme.palette.text.disabled,
    padding: theme.spacing(0, 0.5),
  },
  inputArea: {
    padding: theme.spacing(1, 1.5, 1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    flexShrink: 0,
  },
  inputRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(0.5),
  },
  mediaToolbar: {
    display: "flex",
    gap: theme.spacing(0.5),
    alignItems: "center",
  },
  mediaBtn: {
    width: 32,
    height: 32,
    color: theme.palette.text.secondary,
    "&:hover": { color: "#7c3aed" },
  },
  mediaBtnActive: {
    color: "#ef4444",
    "&:hover": { color: "#dc2626" },
  },
  attachPreview: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "#2d2d2d" : "#f1f5f9",
    border: `1px solid ${theme.palette.divider}`,
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
  imageBubble: {
    maxWidth: 200,
    borderRadius: 10,
    overflow: "hidden",
    cursor: "pointer",
    "& img": { width: "100%", display: "block" },
  },
  audioBubble: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    "& audio": { width: "100%", height: 32 },
  },
  transcriptionLabel: {
    fontSize: "0.7rem",
    color: theme.palette.text.disabled,
    fontStyle: "italic",
  },
  textField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      fontSize: "0.85rem",
    },
  },
  sendButton: {
    backgroundColor: "#7c3aed",
    color: "#ffffff",
    width: 40,
    height: 40,
    "&:hover": {
      backgroundColor: "#6d28d9",
    },
    "&:disabled": {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  actions: {
    padding: theme.spacing(1, 1.5),
    display: "flex",
    gap: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  actionBtn: {
    flex: 1,
    fontSize: "0.75rem",
    textTransform: "none",
    borderRadius: 8,
    padding: theme.spacing(0.75, 1),
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    backgroundColor: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.text.secondary,
    },
  },
  resetBtn: {
    "&:hover": {
      borderColor: "#ef4444",
      color: "#ef4444",
    },
  },
  suggestBtn: {
    "&:hover": {
      borderColor: "#f59e0b",
      color: "#f59e0b",
    },
  },
  suggestions: {
    margin: theme.spacing(0, 1.5, 1.5),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === "dark" ? "#1c1400" : "#fffbeb",
    border: `1px solid ${theme.palette.type === "dark" ? "#78350f44" : "#fcd34d"}`,
    borderRadius: 10,
    fontSize: "0.8rem",
    lineHeight: 1.7,
    color: theme.palette.type === "dark" ? "#fbbf24" : "#92400e",
    whiteSpace: "pre-wrap",
  },
  suggestionsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.5),
    fontWeight: 700,
    fontSize: "0.78rem",
  },
  disclaimerBox: {
    marginTop: 16,
    padding: "8px 12px",
    backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "rgba(255, 193, 7, 0.1)",
    border: `1px solid ${theme.palette.type === "dark" ? "#444" : "rgba(255, 193, 7, 0.3)"}`,
    borderRadius: 8,
    textAlign: "left",
  },
  disclaimerText: {
    color: theme.palette.type === "dark" ? "#fbbf24" : "#d97706",
    display: "block",
    lineHeight: 1.4,
  },
  noNodeMsg: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    textAlign: "center",
  },
  typingDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: theme.palette.text.disabled,
    animation: "$bounce 1.4s infinite ease-in-out both",
    display: "inline-block",
    margin: "0 2px",
  },
  "@keyframes bounce": {
    "0%, 80%, 100%": { transform: "scale(0)" },
    "40%": { transform: "scale(1)" },
  },
}));

// TypingIndicator component
const TypingIndicator = ({ classes }) => (
  <div style={{ display: "flex", alignItems: "center", padding: "4px 0" }}>
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className={classes.typingDot}
        style={{ animationDelay: `${i * 0.16}s` }}
      />
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────
// Main AiSimulatorPanel Component
// ─────────────────────────────────────────────────────────────
const AiSimulatorPanel = ({ flowId, aiNodeData, onClose }) => {
  const classes = useStyles();

  const [messages, setMessages] = useState([]); // [{ role, content, mediaType?, mediaSrc?, transcription? }]
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState("");
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // [Plan 901] Multimodal state
  const [pendingMedia, setPendingMedia] = useState(null); // { type, base64, mimeType, previewUrl, name }
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const imageInputRef = useRef(null);
  const textInputRef = useRef(null);

  const chatEndRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Auto-focus input when loading finishes
  useEffect(() => {
    if (!loading && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [loading]);

  // Extract AI settings from the selected node data
  const aiSettings = aiNodeData?.typebotIntegration || {};
  const modelName = aiSettings?.model || "gpt-4o-mini";
  const ragEnabled = aiSettings?.ragEnabled;
  const hasApiKey = !!aiSettings?.apiKey;

  // ── [Plan 901] Image attach handler ──
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const base64 = dataUrl.split(",")[1];
      setPendingMedia({ type: "image", base64, mimeType: file.type, previewUrl: dataUrl, name: file.name });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // ── [Plan 901] Audio record handler ──
  const handleToggleRecording = async () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => audioChunksRef.current.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const previewUrl = URL.createObjectURL(blob);
        const reader = new FileReader();
        reader.onload = (ev) => {
          const base64 = ev.target.result.split(",")[1];
          setPendingMedia({ type: "audio", base64, mimeType: "audio/webm", previewUrl, name: "audio.webm" });
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch (err) {
      console.error("Microphone error:", err);
    }
  };

  const sendMessage = useCallback(async () => {
    const text = inputText.trim();
    if (!text && !pendingMedia || loading) return;

    // Build display content for the user bubble
    const displayContent = text || (pendingMedia?.type === "image" ? "📷 Imagen adjunta" : "🎙️ Audio adjunto");
    const userMsg = {
      role: "user",
      content: displayContent,
      ...(pendingMedia?.type === "image" && { mediaType: "image", mediaSrc: pendingMedia.previewUrl }),
      ...(pendingMedia?.type === "audio" && { mediaType: "audio", mediaSrc: pendingMedia.previewUrl }),
    };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInputText("");
    const sentMedia = pendingMedia;
    setPendingMedia(null);
    setLoading(true);
    setSuggestions("");
    setShowSuggestions(false);

    // Build API messages (strip mediaType/mediaSrc for the AI history)
    const apiMessages = newMessages.map(m => ({ role: m.role, content: m.content }));

    try {
      const { data } = await api.post("/ai/simulate", {
        flowId,
        messages: apiMessages,
        aiSettings,
        ...(sentMedia && { mediaAttachment: { type: sentMedia.type, base64: sentMedia.base64, mimeType: sentMedia.mimeType } }),
      });

      // [Plan 902] Si el backend devuelve flowMessages[], cada uno es un bubble independiente
      // (igual que WhatsApp envía mensajes separados en producción)
      const newAiMsgs = data.flowMessages && data.flowMessages.length > 0
        ? data.flowMessages.map((text) => ({ role: "assistant", content: text }))
        : [{ role: "assistant", content: data.response, ...(data.transcription && { transcription: data.transcription }) }];

      if (data.transcription) {
        setMessages((prev) => [
          ...prev.slice(0, -1),
          { ...prev[prev.length - 1], transcription: data.transcription },
          ...newAiMsgs,
        ]);
      } else {
        setMessages((prev) => [...prev, ...newAiMsgs]);
      }
    } catch (err) {
      const errText = err?.response?.data?.error || "Error de conexión con la IA.";
      setMessages((prev) => [
        ...prev,
        { role: "error", content: `⚠️ ${errText}` },
      ]);
    } finally {
      setLoading(false);
    }
  }, [inputText, pendingMedia, messages, loading, flowId, aiSettings]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleReset = () => {
    setMessages([]);
    setInputText("");
    setSuggestions("");
    setShowSuggestions(false);
  };

  const handleSuggest = async () => {
    if (messages.length === 0) return;
    setLoadingSuggestions(true);
    setSuggestions("");
    setShowSuggestions(true);

    try {
      const { data } = await api.post("/ai/simulate", {
        flowId,
        messages,
        aiSettings,
        suggestMode: true,
      });
      setSuggestions(data.suggestions || "Sin sugerencias.");
    } catch (err) {
      setSuggestions(
        "⚠️ No se pudieron obtener sugerencias. Verifica tu API Key."
      );
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // No node selected
  if (!aiNodeData) {
    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <span className={classes.headerTitle}>
            <BotIcon style={{ fontSize: 18, color: "#7c3aed" }} />
            Simulador IA
          </span>
          {onClose && (
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </div>
        <div className={classes.noNodeMsg}>
          <BotIcon style={{ fontSize: 48, color: "#e2e8f0" }} />
          <Typography variant="body2" color="textSecondary">
            Selecciona un nodo <strong>IA (OpenAI)</strong> en el flujo para
            activar el simulador.
          </Typography>
        </div>
      </div>
    );
  }


  return (
    <div className={classes.root}>
      {/* ── Header ── */}
      <div className={classes.header}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className={classes.headerTitle}>
            <BotIcon style={{ fontSize: 18, color: "#7c3aed" }} />
            Simulador IA
          </span>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {ragEnabled && (
              <Chip label="RAG ✓" size="small" className={classes.ragBadge} />
            )}
            {messages.length > 0 && (
              <Chip
                label={`${Math.ceil(messages.filter((m) => m.role === "user").length)} turno(s)`}
                size="small"
                style={{ fontSize: "0.65rem", height: 18 }}
              />
            )}
          </div>
        </div>
        {onClose && (
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </div>

      {/* ── Chat Area ── */}
      <div className={classes.chatArea}>
        {messages.length === 0 && !loading && (
          <div className={classes.emptyState}>
            <BotIcon style={{ fontSize: 40, color: "#e2e8f0" }} />
            <Typography variant="body2" color="textSecondary">
              Escribe un mensaje para probar cómo responde el agente
            </Typography>

            <div className={classes.disclaimerBox}>
              <Typography variant="caption" className={classes.disclaimerText}>
                <strong>Nota:</strong> Este entorno está diseñado exclusivamente para probar el comportamiento, prompt y contexto del nodo de IA. No recorre ni simula los demás nodos estándar del diagrama.
              </Typography>
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={classes.messageRow}>
            <span
              className={classes.msgLabel}
              style={{ alignSelf: msg.role === "user" ? "flex-end" : "flex-start" }}
            >
              {msg.role === "user" ? "Tú" : msg.role === "error" ? "Error" : "Agente IA"}
            </span>
            <span
              className={`${classes.bubble} ${
                msg.role === "user" ? classes.userBubble
                : msg.role === "error" ? classes.errorBubble
                : classes.aiBubble
              }`}
              style={{ display: "flex", flexDirection: "column", gap: 6 }}
            >
              {/* Image bubble */}
              {msg.mediaType === "image" && msg.mediaSrc && (
                <div className={classes.imageBubble}>
                  <img src={msg.mediaSrc} alt="adjunto" />
                </div>
              )}
              {/* Audio bubble */}
              {msg.mediaType === "audio" && msg.mediaSrc && (
                <div className={classes.audioBubble}>
                  <audio controls src={msg.mediaSrc} />
                  {msg.transcription && (
                    <span className={classes.transcriptionLabel}>🎙️ "{msg.transcription}"</span>
                  )}
                </div>
              )}
              {/* Text content */}
              {msg.content && !(msg.mediaType === "image" && msg.content === "📷 Imagen adjunta") && (
                <span>{msg.content}</span>
              )}
            </span>
          </div>
        ))}

        {loading && (
          <div className={classes.messageRow}>
            <span className={classes.msgLabel}>Agente IA</span>
            <span className={`${classes.bubble} ${classes.aiBubble}`}>
              <TypingIndicator classes={classes} />
            </span>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* ── Suggestions Panel ── */}
      {showSuggestions && (
        <div className={classes.suggestions}>
          <div className={classes.suggestionsHeader}>
            <span>💡 Sugerencias para mejorar el prompt</span>
            <IconButton
              size="small"
              onClick={() => setShowSuggestions(false)}
              style={{ padding: 2 }}
            >
              <CloseIcon style={{ fontSize: 14 }} />
            </IconButton>
          </div>
          {loadingSuggestions ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "#92400e",
              }}
            >
              <CircularProgress size={14} style={{ color: "#f59e0b" }} />
              Analizando tu prompt...
            </div>
          ) : (
            <span>{suggestions}</span>
          )}
        </div>
      )}

      {/* ── Action Buttons ── */}
      <div className={classes.actions}>
        <Tooltip title="Limpiar conversación y empezar de cero">
          <button
            className={`${classes.actionBtn} ${classes.resetBtn}`}
            onClick={handleReset}
            disabled={loading || messages.length === 0}
          >
            <ResetIcon style={{ fontSize: 14 }} />
            Resetear
          </button>
        </Tooltip>
        <Tooltip title="La IA analizará la conversación y te sugerirá mejoras concretas para tu prompt">
          <button
            className={`${classes.actionBtn} ${classes.suggestBtn}`}
            onClick={handleSuggest}
            disabled={loadingSuggestions || messages.length === 0}
          >
            {loadingSuggestions ? (
              <CircularProgress size={12} style={{ color: "#f59e0b" }} />
            ) : (
              <span>💡</span>
            )}
            Mejorar prompt
          </button>
        </Tooltip>
      </div>

      {/* ── Input Area ── */}
      <div className={classes.inputArea}>
        {/* Media preview bar */}
        {pendingMedia && (
          <div className={classes.attachPreview}>
            {pendingMedia.type === "image" ? <ImageIcon style={{ fontSize: 16, color: "#7c3aed" }} /> : <AudioWaveIcon style={{ fontSize: 16, color: "#7c3aed" }} />}
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pendingMedia.name}</span>
            <IconButton size="small" onClick={() => setPendingMedia(null)} style={{ padding: 2 }}>
              <CloseIcon style={{ fontSize: 14 }} />
            </IconButton>
          </div>
        )}

        {/* Toolbar + Input + Send */}
        <div className={classes.inputRow}>
          {/* Attach image */}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            style={{ display: "none" }}
            onChange={handleImageSelect}
          />
          <Tooltip title="Adjuntar imagen">
            <IconButton
              className={`${classes.mediaBtn} ${pendingMedia?.type === "image" ? classes.mediaBtnActive : ""}`}
              size="small"
              onClick={() => imageInputRef.current?.click()}
              disabled={loading || isRecording}
            >
              <AttachFileIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {/* Record audio */}
          <Tooltip title={isRecording ? "Detener grabación" : "Grabar audio"}>
            <IconButton
              className={`${classes.mediaBtn} ${isRecording ? classes.mediaBtnActive : ""}`}
              size="small"
              onClick={handleToggleRecording}
              disabled={loading || !!pendingMedia}
            >
              {isRecording ? <StopIcon style={{ fontSize: 18 }} /> : <MicIcon style={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>

          <TextField
            inputRef={textInputRef}
            className={classes.textField}
            variant="outlined"
            size="small"
            multiline
            maxRows={4}
            placeholder={pendingMedia ? "Añade un texto (opcional)..." : "Escribe un mensaje de prueba..."}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            inputProps={{ id: "ai-simulator-input" }}
          />
          <IconButton
            className={classes.sendButton}
            onClick={sendMessage}
            disabled={(!inputText.trim() && !pendingMedia) || loading}
            size="small"
          >
            {loading ? (
              <CircularProgress size={16} style={{ color: "#fff" }} />
            ) : (
              <SendIcon style={{ fontSize: 18 }} />
            )}
          </IconButton>
        </div>
      </div>
    </div>
  );
};

export default AiSimulatorPanel;
